import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin } from 'lucide-react';
import { useParams, Navigate } from 'react-router-dom';
import { newsItems } from "../data/newsData";
import { NewsItemType } from '../types';

function updateCountdown(targetDate: string) {
  const target = new Date(targetDate).getTime();
  const now = new Date().getTime();
  const difference = target - now;

  if (difference < 0) {
    return; // Stop countdown if the date is in the past
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  const elements = ['days', 'hours', 'minutes', 'seconds'];
  elements.forEach((id, index) => {
    const el = document.getElementById(id);
    if (el) {
      el.innerText = [days, hours, minutes, seconds][index].toString().padStart(2, '0');
    }
  });
}

export default function NewsDetail() {
  const { slug } = useParams();
  const newsItem = newsItems.find(item => item.slug === slug);

  useEffect(() => {
    if (newsItem?.conferenceDate) {
      updateCountdown(newsItem.conferenceDate);
      const interval = setInterval(() => updateCountdown(newsItem.conferenceDate!), 1000);
      return () => clearInterval(interval);
    }
  }, [newsItem?.conferenceDate]);

  if (!newsItem) {
    return <Navigate to="/news" replace />;
  }

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto px-4 py-12"
    >
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="relative h-64 md:h-96">
          <img
            src={newsItem.image}
            alt={newsItem.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
        
        <div className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {newsItem.category}
            </span>
            <div className="flex items-center text-gray-500">
              <Calendar className="h-4 w-4 mr-2" />
              {newsItem.date}
            </div>
            {newsItem.location && (
              <div className="flex items-center text-gray-500">
                <MapPin className="h-4 w-4 mr-2" />
                {newsItem.location}
              </div>
            )}
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            {newsItem.title}
          </h1>

          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: typeof newsItem.content === 'function' 
                ? newsItem.content(newsItem) 
                : newsItem.content || '' 
            }} 
          />
        </div>
      </div>
    </motion.article>
  );
}